import { useMemo } from 'react';
import { keyBy, sortBy } from 'lodash-es';

import { PublicStudyClient, StudyLibraryClient } from 'jf/api';
import { arrayOf } from 'jf/utils/arrayOf';
import { useClientQuery } from 'jf/utils/useClientQuery';

type DataRecord = {
  responseText: string;
  promptLabel: string;
  teamName?: string;
};

const getCsvData = (data: DataRecord[]) => {
  const headerRow = ['Prompt', 'Response', 'Team'];

  const csvData = [headerRow];

  if (!data.length) {
    csvData.push(arrayOf(headerRow.length).map(() => '')); // empty row
    return csvData;
  }

  for (const datum of data) {
    const dataRow = [datum.promptLabel, datum.responseText, datum.teamName ?? ''];

    csvData.push(dataRow);
  }

  return csvData;
};

const getJsonData = (data: DataRecord[]) => {
  return data.map((datum) => {
    const json = {
      prompt: datum.promptLabel,
      response: datum.responseText,
      team: datum.teamName,
    };

    return json;
  });
};

type UseExportableStudyOpenEndedTextProps = {
  studyRef: string | undefined;
  teamRef: string | undefined;
};

export const useExportableStudyOpenEndedText = (props: UseExportableStudyOpenEndedTextProps) => {
  const { data: prompts } = useClientQuery(StudyLibraryClient.getPrompts);

  const { data: textResponsesAll } = useClientQuery(
    PublicStudyClient.getTextualResponses,
    { studyRef: props.studyRef },
    { enabled: !props.teamRef }
  );
  const { data: textResponsesForTeam } = useClientQuery(
    PublicStudyClient.getTextualResponsesForTeam,
    { studyRef: props.studyRef, teamRef: props.teamRef! },
    { enabled: !!props.teamRef }
  );

  const textResponses = props.teamRef ? textResponsesForTeam : textResponsesAll;

  const [csv, json] = useMemo(() => {
    if (!prompts || !textResponses) {
      return [];
    }

    const promptsBySlug = keyBy(prompts, 'slug');

    let data = textResponses.map((response) => {
      const prompt = promptsBySlug[response.prompt];
      return {
        responseText: response.responseText,
        promptLabel: prompt?.text ?? response.prompt,
        teamName: response.teamName,
      };
    });

    data = sortBy(data, ['promptLabel', 'teamName']);

    return [getCsvData(data), getJsonData(data)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompts, textResponses]);

  return { csv, json };
};
